const ANONYMOUS_NAME = "A Google user";
const READMORE_CHAR_LIMIT = 220;
const MEDIA_THUMB_LIMIT = 3;
const RELEVANCE_CONTENT_CAP = 400;
const RELEVANCE_MEDIA_CAP = 3;
const RELEVANCE_HALF_LIFE_DAYS = 180;

const TIME_UNITS = [
  ["year", 31536000],
  ["month", 2592000],
  ["week", 604800],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

const parseTime = (value) => {
  if (!value) return 0;
  const time = new Date(String(value).replace(" ", "T")).getTime();
  return isNaN(time) ? 0 : time;
};

const timeAgo = (value) => {
  const time = parseTime(value);
  if (!time) return "";
  const seconds = Math.floor((Date.now() - time) / 1000);
  if (seconds < 60) return "just now";
  for (const [name, secs] of TIME_UNITS) {
    const v = Math.floor(seconds / secs);
    if (v >= 1) return `${v} ${name}${v > 1 ? "s" : ""} ago`;
  }
  return "just now";
};

const isLongComment = (text, limit = READMORE_CHAR_LIMIT) => {
  return (text || "").length > limit;
};

const truncateComment = (text, limit = READMORE_CHAR_LIMIT) => {
  text = text || "";
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
};

const isAnonymousReviewer = (review) => {
  return !!review.reviewerIsAnonymous || !review.reviewerName;
};

const reviewerDisplayName = (review, anonymousLabel = ANONYMOUS_NAME) => {
  if (isAnonymousReviewer(review)) return anonymousLabel || ANONYMOUS_NAME;
  return review.reviewerName;
};

const reviewerPhoto = (review) => {
  if (isAnonymousReviewer(review)) return null;
  return review.reviewerPhotoUrl || null;
};

const parseMedia = (media) => {
  if (typeof media === "string" && media) {
    try {
      media = JSON.parse(media);
    } catch (e) {
      return [];
    }
  }
  return Array.isArray(media) ? media : [];
};

const mediaItems = (media) => {
  return parseMedia(media)
    .map((item) => {
      if (typeof item === "string") return { type: "photo", thumbnailUrl: item, videoUrl: null };
      if (!item) return null;
      const isVideo = !!item.videoUrl;
      return {
        type: isVideo ? "video" : "photo",
        thumbnailUrl: item.thumbnailUrl || null,
        videoUrl: isVideo ? item.videoUrl : null,
      };
    })
    .filter((item) => item && item.thumbnailUrl);
};

const mediaUrls = (media) => {
  return mediaItems(media).map((item) => item.thumbnailUrl);
};

const allReviewsUri = (newReviewUri) => {
  if (!newReviewUri) return null;
  try {
    const placeId = new URL(newReviewUri).searchParams.get("placeid");
    if (placeId) return `https://search.google.com/local/reviews?placeid=${placeId}`;
  } catch (e) {
    return newReviewUri;
  }
  return newReviewUri;
};

const relevanceScore = (review) => {
  const contentScore = Math.min((review.comment || "").length, RELEVANCE_CONTENT_CAP) / RELEVANCE_CONTENT_CAP;
  const time = parseTime(review.createdTime);
  const ageDays = Math.max(0, (Date.now() - time) / 86400000);
  const recencyScore = time ? Math.pow(0.5, ageDays / RELEVANCE_HALF_LIFE_DAYS) : 0;
  const mediaScore = Math.min(mediaUrls(review.media).length, RELEVANCE_MEDIA_CAP) / RELEVANCE_MEDIA_CAP;
  const credibilityScore = (isAnonymousReviewer(review) ? 0 : 0.5) + (reviewerPhoto(review) ? 0.5 : 0);
  return contentScore * 0.35 + recencyScore * 0.35 + mediaScore * 0.2 + credibilityScore * 0.1;
};

const sortReviews = (reviews, mode) => {
  const list = [...(reviews || [])];
  if (!mode) return list;
  const byNewest = (a, b) => parseTime(b.createdTime) - parseTime(a.createdTime);
  switch (mode) {
    case "newest":
      return list.sort(byNewest);
    case "oldest":
      return list.sort((a, b) => parseTime(a.createdTime) - parseTime(b.createdTime));
    case "highest":
      return list.sort((a, b) => (b.starRating || 0) - (a.starRating || 0) || byNewest(a, b));
    case "lowest":
      return list.sort((a, b) => (a.starRating || 0) - (b.starRating || 0) || byNewest(a, b));
    case "relevant":
    default: {
      const scores = new Map(list.map((r) => [r, relevanceScore(r)]));
      return list.sort((a, b) => scores.get(b) - scores.get(a) || byNewest(a, b));
    }
  }
};

export {
  READMORE_CHAR_LIMIT,
  MEDIA_THUMB_LIMIT,
  timeAgo,
  isLongComment,
  truncateComment,
  isAnonymousReviewer,
  reviewerDisplayName,
  reviewerPhoto,
  mediaUrls,
  mediaItems,
  allReviewsUri,
  relevanceScore,
  sortReviews,
};
export default {
  READMORE_CHAR_LIMIT,
  MEDIA_THUMB_LIMIT,
  timeAgo,
  isLongComment,
  truncateComment,
  isAnonymousReviewer,
  reviewerDisplayName,
  reviewerPhoto,
  mediaUrls,
  mediaItems,
  allReviewsUri,
  relevanceScore,
  sortReviews,
};
